import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LayoutGrid, RefreshCw } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaId, setCaptchaId] = useState("");
  const [captchaImg, setCaptchaImg] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/auth/captcha");
      setCaptchaId(data.captcha_id);
      setCaptchaImg(data.image);
      setCaptchaAnswer("");
    } catch (e) {
      setError("Could not load captcha. Please try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, captchaRequired ? captchaId : undefined, captchaAnswer);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      if (status === 428) {
        // bot / repeated-failure guard kicked in — show captcha challenge
        setCaptchaRequired(true);
        await loadCaptcha();
        setError("Please complete the verification below to continue.");
      } else {
        setError(apiError(err.response?.data?.detail) || err.message);
        if (captchaRequired) await loadCaptcha(); // refresh single-use challenge
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-zinc-900 p-12 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-zinc-900">
            <LayoutGrid className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight">CLASSIC24 OS</span>
        </div>
        <div className="space-y-5">
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight">
            The operating system for<br />Classic24 Digital Solutions.
          </h1>
          <p className="max-w-md text-sm leading-relaxed text-zinc-400">
            Content delivery, shoot planning and client receivables — every number the
            management needs, in one place, every morning.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-6">
            {[["Content", "Health & progress"], ["Shoots", "Plan & deliver"], ["Payments", "Daily receivables"]].map(
              ([t, s]) => (
                <div key={t} className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-4">
                  <div className="font-heading text-sm font-semibold">{t}</div>
                  <div className="mt-1 text-xs text-zinc-400">{s}</div>
                </div>
              )
            )}
          </div>
        </div>
        <div className="text-xs text-zinc-500">Phase 1 · Agency Operations</div>
      </div>

      <div className="flex w-full flex-col items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-900 text-white">
                <LayoutGrid className="h-5 w-5" />
              </div>
              <span className="font-heading text-lg font-bold">CLASSIC24 OS</span>
            </div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Welcome back</h2>
          <p className="mt-1 text-sm text-zinc-500">Sign in to your agency dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                autoComplete="username"
                placeholder="you@classic24.com"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                autoComplete="current-password"
                placeholder="••••••••"
                data-testid="login-password-input"
                required
              />
            </div>
            {captchaRequired && (
              <div data-testid="login-captcha">
                <Label className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Verification
                </Label>
                <div className="mt-1.5 flex items-center gap-2">
                  {captchaImg && (
                    <img src={captchaImg} alt="captcha" className="h-[58px] w-[170px] rounded-md border border-zinc-200" data-testid="login-captcha-image" />
                  )}
                  <button type="button" onClick={loadCaptcha} className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50" title="New image" data-testid="login-captcha-refresh">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                <Input
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  className="mt-2"
                  placeholder="Type the characters above"
                  autoComplete="off"
                  autoCapitalize="characters"
                  data-testid="login-captcha-input"
                  required
                />
              </div>
            )}
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
